/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import Button from "../../components/Shared/Button";
import { useParams } from "react-router-dom";
import Input from "../../components/Shared/Input";
import { ButtonTypes } from "../../utils/consts";
import TextArea from "../../components/Shared/TextArea";
import MultipleUpload from "../../components/Shared/MultipleUpload";
import { toast } from "react-toastify";
import Upload from "../../components/Shared/Upload";
import { AddMockupExam } from "../../api/Certificates/AddMockup";
interface Choice {
  answer: string;
  isCorrect: boolean;
}

interface Question {
  id: number;
  name: string;
  choices: Choice[];
  correctAnswerIndex: number;
  explanation: string;
  figure: string[];
  attachment: string;
}

const AddMockup = () => {
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setErrors] = useState({
    title: '',
    questions: [] as string[],
  });


  const [mcqQuestions, setMcqQuestions] = useState<Question[]>([
    {
      id: 1,
      name: "",
      choices: [
        { answer: "", isCorrect: true },
        { answer: "", isCorrect: false },
        { answer: "", isCorrect: false },
        { answer: "", isCorrect: false },
      ],
      correctAnswerIndex: 0,
      explanation: "",
      attachment: "",
      figure: [],
    },
  ]);


  const [nextMcqId, setNextMcqId] = useState(2);

  const validateForm = () => {
    let hasError = false;
    const questionErrors: string[] = [];

    // Validate quiz title
    if (!quizTitle.trim()) {
      setErrors(prev => ({ ...prev, title: "Quiz title is required." }));
      hasError = true;
    } else {
      setErrors(prev => ({ ...prev, title: "" }));
    }

    // Validate questions based on the quiz type
      mcqQuestions.forEach((question, index) => {
        if (!question.name.trim() || question.choices.some(choice => !choice.answer.trim())) {
          questionErrors[index] = "All questions and choices are required.";
          hasError = true;
        } else {
          questionErrors[index] = "";
        }
      });
    setErrors(prev => ({ ...prev, questions: questionErrors }));
    return !hasError;
  };

  const [quizTitle, setQuizTitle] = useState("");
  const [quizDuration, setQuizDuration] = useState("");

  const handleAddQuestion = () => {
      const newQuestion: Question = {
        id: nextMcqId,
        name: "",
        choices: [
          { answer: "", isCorrect: true },
          { answer: "", isCorrect: false },
          { answer: "", isCorrect: false },
          { answer: "", isCorrect: false },
        ],
        correctAnswerIndex: 0,
        explanation: "",
        attachment: "",
        figure: [],
      };
      setMcqQuestions([...mcqQuestions, newQuestion]);
      setNextMcqId(nextMcqId + 1);
  };

  const handleDeleteQuestion = (questionId: number) => {
      setMcqQuestions(mcqQuestions.filter((question) => question.id !== questionId));
  };

  const handleQuestionFieldChange = (
    questionIndex: number,
    field: string,
    value: string | boolean,
    choiceIndex?: number,
  ) => {
      setMcqQuestions((prevQuestions) =>
        prevQuestions.map((question, index) => {
          if (index === questionIndex) {
            if (typeof choiceIndex !== "undefined") {
              const updatedChoices = question.choices.map((choice, idx) =>
                idx === choiceIndex ? { ...choice, [field]: value } : choice
              );
              return { ...question, choices: updatedChoices };
            } else {
              return { ...question, [field]: value };
            }
          }
          return question;
        })
      );
  };

  const handleCorrectAnswerChange = (
    questionIndex: number,
    selectedChoiceIndex: number,
  ) => {
      setMcqQuestions((prevQuestions) =>
        prevQuestions.map((question, index) => {
          if (index === questionIndex) {
            const updatedChoices = question.choices.map((choice, idx) => ({
              ...choice,
              isCorrect: idx === selectedChoiceIndex,
            }));
            return {
              ...question,
              choices: updatedChoices,
              correctAnswerIndex: selectedChoiceIndex,
            };
          }
          return question;
        })
      );
  };

  const { mutate } = useMutation({
    //@ts-ignore
    mutationFn: AddMockupExam,
    onSuccess: () => {
      setIsSubmitting(true); 
      toast.success("Mockup Added successfully");
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const handleSubmit = (event: { preventDefault: () => void }) => {
    event.preventDefault();

    if (validateForm()) {
        const payload = {
          title: quizTitle,
          duration:quizDuration,
          certificateExamId:id,
          questions: [
            ...mcqQuestions.map(question => ({
              question: question.name,
              explanation: question.explanation,
              choices: question.choices.map(choice => ({
                answer: choice.answer,
                isCorrect: choice.isCorrect,
              })),
              attachment: question.attachment,
              figure: question.figure,
            })),
          ],
        };
      setIsSubmitting(true);
      //@ts-expect-error
      mutate(payload);
    }
  };

  const handleFileUploaded = (url: string, questionId: number) => {
      setMcqQuestions((prevQuestions) => {
        const updatedQuestions = prevQuestions.map((question) =>
          question.id === questionId
            ? { ...question, figure: [...question.figure, url] }
            : question
        );
        return updatedQuestions;
      });
  };

  const handleAttachmentUpload = (url: string, questionId: number) => {
      setMcqQuestions((prevQuestions) => {
        const updatedQuestions = prevQuestions.map((question) =>
          question.id === questionId
            ? { ...question, attachment: url }
            : question
        );
        return updatedQuestions;
      });
  };


  return (
    <div className="py-7">
      <h1 className="text-burntBlack font-bold text-[32px] mb-6">
        Create new Mockup quiz
      </h1>
      <form
        onSubmit={handleSubmit}
        className="p-8 rounded-3xl shadow bg-white border border-black/15 min-h-[70vh] space-y-5"
      >
          <>
              <div className="w-full max-w-[544px] mx-auto">
                <Input
                  className="text-lg font-semibold mb-10"
                  inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                  title="Mocup Title"
                  placeholder=""
                  value={quizTitle}
                  name="title"
                  onChange={(e) => setQuizTitle(e.target.value)}
                />
                <Input
                  className="text-lg font-semibold mb-10"
                  inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                  title="Mocup Duration"
                  placeholder=""
                  value={quizDuration}
                  name="duration"
                  onChange={(e) => setQuizDuration(e.target.value)}
                />

                <div>
                  <h1 className="text-2xl font-bold mb-2.5">Questions</h1>
                  <p className="text-lightGray font-medium">
                    Nice text goes here to explain the question creation part like
                    never before
                  </p>
                </div>
                <div>
                  {mcqQuestions.map((question, questionIndex) => (
                    <div key={question.id}>
                      <div className="mt-9">
                        <Input
                          className="text-lg font-semibold"
                          inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded mb-10"
                          title="Question name"
                          placeholder=""
                          name="name"
                          value={question.name}
                          onChange={(e) =>
                            handleQuestionFieldChange(
                              questionIndex,
                              "name",
                              e.target.value,
                              undefined,
                            )
                          }
                          type="text"
                        />
                        <p className="text-lightGray font-medium mb-4">
                          Please mark the correct answer among them
                        </p>
                        <div className="grid-cols-2 grid gap-x-6 gap-y-4">
                          {question.choices.map((choice, choiceIndex) => (
                            <div key={`choice-${choiceIndex}`}>
                              <div className="flex items-center justify-between">
                                <label
                                  htmlFor={`choice-radio-${questionIndex}-${choiceIndex}`}
                                  className="text-lg font-semibold"
                                >
                                  {`Choice ${choiceIndex + 1}`}
                                </label>
                                <input
                                  id={`choice-radio-${questionIndex}-${choiceIndex}`}
                                  type="radio"
                                  value={choiceIndex}
                                  name={`question-${question.id}-choices`}
                                  checked={
                                    choiceIndex === question.correctAnswerIndex
                                  }
                                  onChange={() =>
                                    handleCorrectAnswerChange(
                                      questionIndex,
                                      choiceIndex,
                                    )
                                  }
                                  className="w-5 h-5 text-primary bg-gray-100 border-black/30 border-2 focus:ring-0"
                                />
                              </div>
                              <Input
                                className="text-lg font-semibold"
                                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded mb-10"
                                placeholder=""
                                name={`choice-${choiceIndex}`}
                                value={choice.answer}
                                onChange={(e) =>
                                  handleQuestionFieldChange(
                                    questionIndex,
                                    "answer",
                                    e.target.value,
                                    choiceIndex,
                                  )
                                }
                                type="text"
                              />
                            </div>
                          ))}
                        </div>
                        <TextArea
                          className="text-lg font-semibold mb-10"
                          inputStyles="!bg-[#F5F6F8] h-24 border-black/15 !rounded"
                          title="Explanation (Optional)"
                          placeholder=""
                          name="explanation"
                          value={question.explanation}
                          onChange={(e) =>
                            handleQuestionFieldChange(
                              questionIndex,
                              "explanation",
                              e.target.value,
                              undefined,
                            )
                          }
                        />
                        <MultipleUpload
                          folder="courses_images"
                          onFileUploaded={(url) =>
                            handleFileUploaded(url, question.id)
                          }
                          questionId={question.id}
                        >
                          <Input
                            upload={true}
                            className="text-lg font-semibold mb-10"
                            inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                            placeholder=""
                            title="Figures"
                            name="img_url"
                            type="text"
                          />
                        </MultipleUpload>
                        <Upload
                          acceptedFiles={{ "pdf": [] }}
                          folder="quiz_resources"
                          onFileUploaded={(url) => handleAttachmentUpload(url, question.id)}

                        >
                          <Input
                            upload={true}
                            className="text-lg font-semibold"
                            inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                            placeholder=""
                            title="Attachments"
                            name="attachment"
                            type="text"
                          />
                        </Upload>

                      </div>
                      <div className="flex justify-end gap-5 mt-10">
                        <Button
                          onClick={() => handleDeleteQuestion(question.id)}
                          className={`before:!bg-primary hover:text-white !text-base !font-bold !h-12 w-40 !border-primary !border-2 text-primary `}
                          type={ButtonTypes.BUTTON}
                          variant="transparent"
                        >
                          Delete
                        </Button>
                        <Button
                          onClick={() => handleAddQuestion()}
                          type={ButtonTypes.BUTTON}
                          variant="primary"
                          className="!h-12 w-40 !text-base !font-bold "
                        >
                          Add New Question
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            
            <div className="flex justify-end w-full">
              <Button
                type={ButtonTypes.SUBMIT}
                disabled={isSubmitting}
                className={`mt-8 w-[160px] !h-11 !text-base font-bold `}
                variant="primary"
              >
                Send Replies
              </Button>
            </div>
          </>
      </form>
    </div>
  );
};

export default AddMockup;
